import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Checkbox,
  Label,
  RadioGroup,
  RadioGroupItem,
  Spinner,
  Textarea,
} from '@agion/ui'
import { History, Lock, RefreshCw, Route } from 'lucide-react'
import type { RolloutControlProps } from './rollout_catalog'
import { isRolloutOutcomeUncertain, isRolloutRevisionConflict, RolloutPageError, rolloutErrorText } from './rollout_refusal'
import { isCrossOrganizationTarget, type RolloutTarget } from './rollout_target'
import {
  changeMissionStackRolloutPolicy,
  getMissionStackRolloutPolicy,
  listMissionStackRolloutHistory,
  type MissionStackRolloutHistoryEntry,
  type MissionStackRolloutLevel,
  type MissionStackRolloutPolicy,
} from './infra_mission_stack_rollout.service'

const rolloutLevels: readonly MissionStackRolloutLevel[] = ['off', 'authoring', 'execution']
const historyDateOptions: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' }

function rolloutLevelControlID(level: MissionStackRolloutLevel): string {
  return `mission-stack-rollout-${level}`
}

const levelBadgeVariant: Record<MissionStackRolloutLevel, 'default' | 'warning' | 'success'> = {
  off: 'default',
  authoring: 'warning',
  execution: 'success',
}

export function MissionStackRolloutCard({ organization, activeOrganizationId }: RolloutControlProps) {
  const { t, i18n } = useTranslation('pages')
  const target = useMemo<RolloutTarget>(
    () => ({ organizationId: organization.id, activeOrganizationId }),
    [organization.id, activeOrganizationId],
  )
  const mismatchKey = isCrossOrganizationTarget(target)
    ? 'rolloutControls.target.organizationMismatch'
    : 'rolloutControls.missionStack.organizationMismatch'
  const cardOrganizationRef = useRef(organization.id)
  const loadGeneration = useRef(0)
  const mutationGeneration = useRef(0)
  cardOrganizationRef.current = organization.id
  const [policy, setPolicy] = useState<MissionStackRolloutPolicy | null>(null)
  const [history, setHistory] = useState<MissionStackRolloutHistoryEntry[]>([])
  const [targetLevel, setTargetLevel] = useState<MissionStackRolloutLevel>('off')
  const [reason, setReason] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [historyWarning, setHistoryWarning] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const load = useCallback(async () => {
    const generation = ++loadGeneration.current
    const requestedOrganizationId = organization.id
    setIsLoading(true)
    setError(null)
    setHistoryWarning(null)
    setSaved(false)
    setPolicy(null)
    setHistory([])
    try {
      const currentPolicy = await getMissionStackRolloutPolicy(target)
      if (generation !== loadGeneration.current || cardOrganizationRef.current !== requestedOrganizationId) return
      if (currentPolicy.organizationId !== requestedOrganizationId) {
        throw new RolloutPageError(t(mismatchKey))
      }
      setPolicy(currentPolicy)
      setTargetLevel(currentPolicy.level)
      setReason('')
      setConfirmed(false)
      try {
        const currentHistory = await listMissionStackRolloutHistory(target)
        if (generation !== loadGeneration.current || cardOrganizationRef.current !== requestedOrganizationId) return
        setHistory(currentHistory)
      } catch {
        if (generation !== loadGeneration.current || cardOrganizationRef.current !== requestedOrganizationId) return
        setHistoryWarning(t('rolloutControls.missionStack.historyRefreshError'))
      }
    } catch (loadError) {
      if (generation !== loadGeneration.current || cardOrganizationRef.current !== requestedOrganizationId) return
      setError(rolloutErrorText(loadError, t, 'rolloutControls.loadError'))
    } finally {
      if (generation === loadGeneration.current && cardOrganizationRef.current === requestedOrganizationId) {
        setIsLoading(false)
      }
    }
  }, [mismatchKey, organization.id, t, target])

  const invalidatePendingRequests = useCallback(() => {
    loadGeneration.current++
    mutationGeneration.current++
  }, [])

  useEffect(() => {
    mutationGeneration.current++
    setIsSaving(false)
    void load()
    return invalidatePendingRequests
  }, [invalidatePendingRequests, load])

  const reasonBytes = useMemo(() => new TextEncoder().encode(reason.trim()).length, [reason])
  // The staff route answers environmentLocked; the organisation's own route
  // does not send it, so its absence keeps the card writable as before.
  const locked = policy?.environmentLocked === true
  const canApply = policy !== null
    && !locked
    && targetLevel !== policy.level
    && reasonBytes > 0
    && reasonBytes <= 500
    && confirmed
    && !isSaving

  const apply = async () => {
    if (!policy || !canApply) return
    const generation = ++mutationGeneration.current
    const requestedOrganizationId = organization.id
    setIsSaving(true)
    setError(null)
    setHistoryWarning(null)
    setSaved(false)
    try {
      const updated = await changeMissionStackRolloutPolicy({
        expectedRevision: policy.revision,
        level: targetLevel,
        reason: reason.trim(),
        confirmed: true,
      }, target)
      if (generation !== mutationGeneration.current || cardOrganizationRef.current !== requestedOrganizationId) return
      if (updated.organizationId !== requestedOrganizationId) {
        throw new RolloutPageError(t(mismatchKey))
      }
      setPolicy(updated)
      setTargetLevel(updated.level)
      setReason('')
      setConfirmed(false)
      setSaved(true)
      try {
        const currentHistory = await listMissionStackRolloutHistory(target)
        if (generation !== mutationGeneration.current || cardOrganizationRef.current !== requestedOrganizationId) return
        setHistory(currentHistory)
      } catch {
        if (generation !== mutationGeneration.current || cardOrganizationRef.current !== requestedOrganizationId) return
        setHistoryWarning(t('rolloutControls.missionStack.historyRefreshError'))
      }
    } catch (saveError) {
      if (generation !== mutationGeneration.current || cardOrganizationRef.current !== requestedOrganizationId) return
      if (isRolloutRevisionConflict(saveError)) {
        await load()
        if (generation === mutationGeneration.current && cardOrganizationRef.current === requestedOrganizationId) {
          setError(t('rolloutControls.missionStack.conflict'))
        }
      } else if (isRolloutOutcomeUncertain(saveError)) {
        await load()
        if (generation === mutationGeneration.current && cardOrganizationRef.current === requestedOrganizationId) {
          setError(rolloutErrorText(saveError, t, 'rolloutControls.missionStack.saveError'))
        }
      } else {
        setError(rolloutErrorText(saveError, t, 'rolloutControls.missionStack.saveError'))
      }
    } finally {
      if (generation === mutationGeneration.current && cardOrganizationRef.current === requestedOrganizationId) {
        setIsSaving(false)
      }
    }
  }

  return (
    <Card>
      <CardHeader className="border-b border-slate-100 pb-4 dark:border-slate-800">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-violet-50 p-2 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300">
              <Route className="h-5 w-5" aria-hidden="true" />
            </div>
            <div>
              <CardTitle>{t('rolloutControls.missionStack.title')}</CardTitle>
              <CardDescription className="mt-1">{t('rolloutControls.missionStack.description')}</CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="purple">{t('rolloutControls.missionStack.organizationScoped')}</Badge>
            {policy && (
              <Badge variant={levelBadgeVariant[policy.level]}>
                {t(`rolloutControls.missionStack.levels.${policy.level}.label`)}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {isLoading ? (
          <div className="flex items-center justify-center py-12"><Spinner /></div>
        ) : error && !policy ? (
          <Alert variant="error">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span>{error}</span>
              <Button variant="secondary" size="sm" onClick={() => void load()}>
                <RefreshCw className="mr-1 h-4 w-4" />{t('rolloutControls.refresh')}
              </Button>
            </div>
          </Alert>
        ) : policy ? (
          <>
            {error && <Alert variant="error">{error}</Alert>}
            {saved && <Alert variant="success">{t('rolloutControls.missionStack.saved')}</Alert>}
            {historyWarning && <Alert variant="warning">{historyWarning}</Alert>}

            {locked ? (
              <Alert variant="warning">
                <span className="flex items-center gap-2"><Lock className="h-4 w-4" aria-hidden="true" />{t('rolloutControls.missionStack.lockedNotice')}</span>
              </Alert>
            ) : (
              <Alert variant="info">{t('rolloutControls.missionStack.existingRevisionNotice')}</Alert>
            )}

            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,0.7fr)]">
              <div>
                <div className="mb-2 text-sm font-medium text-slate-800 dark:text-slate-200">
                  {t('rolloutControls.missionStack.targetLevel')}
                </div>
                <RadioGroup
                  value={targetLevel}
                  disabled={locked}
                  onValueChange={(value) => {
                    setTargetLevel(value as MissionStackRolloutLevel)
                    setConfirmed(false)
                    setSaved(false)
                  }}
                  className="space-y-2"
                >
                  {rolloutLevels.map((level) => {
                    const id = rolloutLevelControlID(level)
                    const selected = targetLevel === level
                    return (
                      <div
                        key={level}
                        className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${selected
                          ? 'border-violet-400 bg-violet-50/70 dark:border-violet-600 dark:bg-violet-950/20'
                          : 'border-slate-200 dark:border-slate-700'}`}
                      >
                        <RadioGroupItem
                          id={id}
                          value={level}
                          disabled={locked}
                          aria-label={t(`rolloutControls.missionStack.levels.${level}.label`)}
                          className="mt-0.5"
                        />
                        <Label htmlFor={id} className="min-w-0 flex-1 cursor-pointer">
                          <span className="block text-sm font-medium text-slate-900 dark:text-slate-100">
                            {t(`rolloutControls.missionStack.levels.${level}.label`)}
                          </span>
                          <span className="mt-1 block text-xs leading-5 text-slate-500 dark:text-slate-400">
                            {t(`rolloutControls.missionStack.levels.${level}.description`)}
                          </span>
                        </Label>
                      </div>
                    )
                  })}
                </RadioGroup>
              </div>

              <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
                <dl className="space-y-3 text-sm">
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-slate-500">{t('rolloutControls.missionStack.currentOrganization')}</dt>
                    <dd className="mt-1 font-medium text-slate-900 dark:text-slate-100">{organization.name}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-slate-500">{t('rolloutControls.missionStack.currentLevel')}</dt>
                    <dd className="mt-1">{t(`rolloutControls.missionStack.levels.${policy.level}.label`)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-slate-500">{t('rolloutControls.missionStack.revision')}</dt>
                    <dd className="mt-1 font-mono">{policy.revision}</dd>
                  </div>
                </dl>
              </div>
            </div>

            {!locked && (
              <div>
                <Textarea
                  id="mission-stack-rollout-reason"
                  label={t('rolloutControls.missionStack.reasonLabel')}
                  placeholder={t('rolloutControls.missionStack.reasonPlaceholder')}
                  value={reason}
                  rows={3}
                  onChange={(event) => {
                    setReason(event.target.value.replace(/[\r\n\t]+/g, ' '))
                    setSaved(false)
                  }}
                  error={reasonBytes > 500 ? t('rolloutControls.missionStack.reasonTooLong') : undefined}
                />
                <div className="mt-1 text-right text-xs text-slate-400">
                  {t('rolloutControls.missionStack.reasonBytes', { bytes: reasonBytes })}
                </div>
              </div>
            )}

            {!locked && targetLevel !== policy.level && (
              <Checkbox
                id="mission-stack-rollout-confirmation"
                checked={confirmed}
                onCheckedChange={(checked) => setConfirmed(checked === true)}
                label={t('rolloutControls.missionStack.confirmation', {
                  organization: organization.name,
                  level: t(`rolloutControls.missionStack.levels.${targetLevel}.label`),
                })}
              />
            )}

            <div className="rounded-lg border border-dashed border-slate-300 p-4 dark:border-slate-700">
              <div className="text-sm font-medium text-slate-800 dark:text-slate-200">{t('rolloutControls.missionStack.retirementTitle')}</div>
              <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">{t('rolloutControls.missionStack.retirementDescription')}</p>
            </div>

            <div>
              <div className="mb-3 flex items-center gap-2 text-sm font-medium text-slate-800 dark:text-slate-200">
                <History className="h-4 w-4" aria-hidden="true" />
                {t('rolloutControls.missionStack.historyTitle')}
              </div>
              {history.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">{t('rolloutControls.missionStack.noHistory')}</p>
              ) : (
                <ol className="divide-y divide-slate-100 rounded-lg border border-slate-200 dark:divide-slate-800 dark:border-slate-700">
                  {history.map((entry) => (
                    <li key={entry.revision} className="p-3 text-sm">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-medium text-slate-800 dark:text-slate-200">
                          {t('rolloutControls.missionStack.historyTransition', {
                            from: t(`rolloutControls.missionStack.levels.${entry.previousLevel}.label`),
                            to: t(`rolloutControls.missionStack.levels.${entry.level}.label`),
                          })}
                        </span>
                        <time className="text-xs text-slate-400" dateTime={entry.changedAt}>
                          {new Intl.DateTimeFormat(i18n.language, historyDateOptions).format(new Date(entry.changedAt))}
                        </time>
                      </div>
                      <p className="mt-1 text-slate-600 dark:text-slate-400">{entry.reason}</p>
                      <div className="mt-1 font-mono text-[11px] text-slate-400">
                        {t('rolloutControls.missionStack.historyEvidence', { actor: entry.actorId, revision: entry.revision })}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </>
        ) : null}
      </CardContent>

      {policy && !isLoading && !locked && (
        <CardFooter className="justify-end border-t border-slate-100 pt-4 dark:border-slate-800">
          <Button variant="primary" disabled={!canApply} isLoading={isSaving} onClick={() => void apply()}>
            {t('rolloutControls.missionStack.apply')}
          </Button>
        </CardFooter>
      )}
    </Card>
  )
}
